import { type ReadonlyURLSearchParams } from 'next/navigation'
import { useState } from 'react'

import type { TicketPhoto } from '@/api/dto/ticket'
import Shared from '@/shared'
import { formattingDateMMdd, yyMMdd } from '@/shared/utils/date'

interface ShareListViewModel {
  texts: object
  states: { isToastOpen: boolean; toastMessage: string }
  setters: { setIsToastOpen: React.Dispatch<React.SetStateAction<boolean>> }
  handlers: {
    handleShareKakao: () => void
    handleCopyLink: () => Promise<void>
  }
}

interface UseShareListViewModelProps {
  type: string
  seq: number
  parkinglotName: string
  parkinglotAddress: string
  searchParams: ReadonlyURLSearchParams
  pathname: string
  price?: number
  photo?: TicketPhoto
}

const useShareListViewModel = ({
  type,
  seq,
  parkinglotName,
  parkinglotAddress,
  searchParams,
  pathname,
  price,
  photo
}: UseShareListViewModelProps): ShareListViewModel => {
  const webAppUrl = process.env.NEXT_PUBLIC_WEBAPP_HOST
  const lowerCaseTypeForCopy = type.toLowerCase()
  const [isToastOpen, setIsToastOpen] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const defaultImage = `${webAppUrl}/images/light_img_linkbanner.png`

  const showToast = (message: string) => {
    setToastMessage(message)
    setIsToastOpen(true)
  }

  const copyMessageByType: Record<string, string> = {
    t: '주차권 링크가 복사됐습니다.',
    p: '주차장 링크가 복사됐습니다.',
    s: '주차장 링크가 복사됐습니다.'
  }

  const copyToClipboard = async (url: string, successMessage: string) => {
    try {
      await Shared.Utils.handleCopyClipBoard(url)
      showToast(successMessage)
    } catch (e) {
      showToast('링크 복사에 실패했습니다. 다시 시도해주세요.')
    }
  }

  const handleShareKakao = () => {
    if (window.Kakao.isInitialized()) {
      const isAirport = pathname.includes('airport')
      const isParkinglot = pathname.includes('map') || pathname.includes('p')
      let queryParams, sDate, eDate
      if (isAirport && searchParams.get('sDate') && searchParams.get('eDate')) {
        queryParams = searchParams.toString()
        sDate = formattingDateMMdd(searchParams.get('sDate'), yyMMdd)
        eDate = formattingDateMMdd(searchParams.get('eDate'), yyMMdd)
      }

      const description = isAirport
        ? `${sDate} ~ ${eDate}\n${price?.toLocaleString()}원`
        : isParkinglot
          ? `${parkinglotAddress}`
          : `${parkinglotAddress}\n${price?.toLocaleString()}원`

      const baseUrl = isAirport
        ? `${webAppUrl}/airport/ticket/${seq}?${queryParams}`
        : `${webAppUrl}/${lowerCaseTypeForCopy}/${seq}`

      const buttonTitle = isAirport ? '공항주차권 보러가기' : isParkinglot ? '주차장 보러가기' : '주차권 보러가기'

      window.Kakao.Share.sendDefault({
        objectType: 'feed',
        content: {
          title: parkinglotName,
          description,
          imageUrl: photo?.fileName || defaultImage,
          link: {
            mobileWebUrl: baseUrl,
            webUrl: baseUrl
          }
        },
        buttons: [
          {
            title: buttonTitle,
            link: {
              mobileWebUrl: baseUrl,
              webUrl: baseUrl
            }
          }
        ]
      })
    } else {
      setToastMessage('초기화 과정에서 오류가 발생했습니다. 다시 시도해주세요.')
      setIsToastOpen(true)
    }
  }

  const handleCopyLink = async () => {
    const isAirport = pathname.includes('airport')

    if (isAirport && searchParams) {
      const queryParams = searchParams.toString()
      const url = `${webAppUrl}/airport/ticket/${seq}?${queryParams}`
      await copyToClipboard(url, '공항 주차권 링크가 복사됐습니다.')
      return
    }

    const url = `${webAppUrl}/${lowerCaseTypeForCopy}/${seq}`
    const message = copyMessageByType[lowerCaseTypeForCopy] ?? '링크가 복사됐습니다.'
    await copyToClipboard(url, message)
  }

  return {
    texts: {},
    states: { isToastOpen, toastMessage },
    setters: { setIsToastOpen },
    handlers: { handleShareKakao, handleCopyLink }
  }
}

export { useShareListViewModel }
